// Admin tab — Documents management: upload (any media), CRUD, classify by category
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const CATEGORY_LABELS_FR = {
  history: "Histoire & Archives",
  patrimony: "Patrimoine & Plans",
  association: "Vie de l'Association",
  millenium: "Fêtes Millenium 2027",
};
const CATEGORY_LABELS_EN = {
  history: "History & Archives",
  patrimony: "Heritage & Maps",
  association: "Association life",
  millenium: "Millenium 2027 Festivities",
};

const emptyDoc = () => ({
  title_fr: "", title_en: "",
  description_fr: "", description_en: "",
  category: "history",
  file_url: "", format: "file", size: 0,
  published: true, featured: false,
});

const formatSize = (bytes) => {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const resolveUrl = (u) => {
  if (!u) return "#";
  if (u.startsWith("http")) return u;
  return `${BACKEND}${u}`;
};

export default function DocumentsTab({ locale }) {
  const isFr = locale === "fr";
  const labels = isFr ? CATEGORY_LABELS_FR : CATEGORY_LABELS_EN;
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/documents");
      setItems(data.items || []);
      setCategories(data.categories || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePublished = async (d) => {
    await api.patch(`/admin/documents/${d.id}`, { published: !d.published });
    load();
  };

  const toggleFeatured = async (d) => {
    await api.patch(`/admin/documents/${d.id}`, { featured: !d.featured });
    load();
  };

  const remove = async (d) => {
    const title = d.title?.fr || d.title?.en || "(sans titre)";
    if (!window.confirm(isFr ? `Supprimer définitivement « ${title} » ?` : `Permanently delete "${title}"?`)) return;
    await api.delete(`/admin/documents/${d.id}`);
    load();
  };

  const grouped = categories.reduce((acc, c) => {
    acc[c] = items.filter((it) => it.category === c);
    return acc;
  }, {});

  return (
    <div data-testid="documents-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-3xl">{isFr ? "Documents & médias" : "Documents & media"}</h2>
        <button
          data-testid="doc-new-btn"
          className="btn btn-primary"
          onClick={() => { setCreating(true); setEditing(emptyDoc()); }}
        >
          {isFr ? "+ Nouveau document" : "+ New document"}
        </button>
      </div>

      {editing && (
        <DocumentEditor
          initial={editing}
          isCreate={creating}
          isFr={isFr}
          categories={categories}
          labels={labels}
          onCancel={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}

      {loading && <div className="font-display italic" style={{ color: "var(--muted)" }}>…</div>}

      {!loading && categories.map((cat) => (
        <div key={cat} data-testid={`doc-cat-${cat}`}>
          <div className="deco-rule mb-3">
            <span className="font-caps text-[0.78rem]">{labels[cat] || cat}</span>
            <span className="ml-3 font-body text-xs" style={{ color: "var(--muted)" }}>
              ({(grouped[cat] || []).length})
            </span>
          </div>
          <div className="space-y-2">
            {(grouped[cat] || []).length === 0 && (
              <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>
                {isFr ? "Aucun document." : "No document."}
              </div>
            )}
            {(grouped[cat] || []).map((d) => (
              <div
                key={d.id}
                data-testid={`doc-row-${d.id}`}
                className="flex items-center gap-3 p-3 bg-white border"
                style={{ borderColor: "var(--border)" }}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-body text-base truncate">
                    {d.featured && <span style={{ color: "var(--gold)" }} title="À la une">★ </span>}
                    {d.title?.fr || d.title?.en}
                  </div>
                  <div className="flex gap-3 mt-1 font-caps text-[0.6rem]" style={{ color: "var(--muted)" }}>
                    <span>{(d.format || "file").toUpperCase()}</span>
                    {d.size > 0 && <span>· {formatSize(d.size)}</span>}
                    {d.external && <span>· EXT</span>}
                    <span data-testid={`doc-count-${d.id}`}>· {d.download_count || 0} {isFr ? "vues" : "views"}</span>
                    {!d.published && <span style={{ color: "var(--cinnabar)" }}>· {isFr ? "MASQUÉ" : "HIDDEN"}</span>}
                  </div>
                </div>
                <a
                  href={resolveUrl(d.file_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline btn-xs"
                  data-testid={`doc-open-${d.id}`}
                >
                  {isFr ? "Voir" : "View"}
                </a>
                <button
                  className="btn btn-outline btn-xs"
                  onClick={() => toggleFeatured(d)}
                  data-testid={`doc-featured-${d.id}`}
                  style={d.featured ? { color: "var(--gold)", borderColor: "var(--gold)" } : {}}
                  title={isFr ? "À la une" : "Featured"}
                >
                  {d.featured ? "★" : "☆"}
                </button>
                <button
                  className="btn btn-outline btn-xs"
                  onClick={() => togglePublished(d)}
                  data-testid={`doc-toggle-${d.id}`}
                >
                  {d.published ? (isFr ? "Masquer" : "Hide") : (isFr ? "Publier" : "Publish")}
                </button>
                <button
                  className="btn btn-outline btn-xs"
                  onClick={() => { setEditing({
                    id: d.id,
                    title_fr: d.title?.fr || "", title_en: d.title?.en || "",
                    description_fr: d.description?.fr || "", description_en: d.description?.en || "",
                    category: d.category, file_url: d.file_url,
                    format: d.format, size: d.size, published: d.published,
                    featured: d.featured || false,
                  }); setCreating(false); }}
                  data-testid={`doc-edit-${d.id}`}
                >
                  {isFr ? "Modifier" : "Edit"}
                </button>
                <button
                  className="btn btn-outline btn-xs"
                  style={{ color: "var(--cinnabar)" }}
                  onClick={() => remove(d)}
                  data-testid={`doc-delete-${d.id}`}
                >
                  {isFr ? "Supprimer" : "Delete"}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DocumentEditor({ initial, isCreate, isFr, categories, labels, onCancel, onSaved }) {
  const [d, setD] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInput = useRef(null);

  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setD((s) => ({
        ...s,
        file_url: data.url,
        format: data.format,
        size: data.size,
        title_fr: s.title_fr || file.name.replace(/\.[^.]+$/, ""),
      }));
    } catch (e) {
      setError(e?.response?.data?.detail || (isFr ? "Échec de l'envoi" : "Upload failed"));
    } finally { setUploading(false); }
  };

  const save = async () => {
    setError(null);
    if (!d.title_fr.trim()) {
      setError(isFr ? "Le titre FR est requis." : "FR title is required."); return;
    }
    if (!d.file_url) {
      setError(isFr ? "Aucun fichier sélectionné." : "No file selected."); return;
    }
    try {
      if (d.id) {
        await api.patch(`/admin/documents/${d.id}`, {
          title_fr: d.title_fr, title_en: d.title_en,
          description_fr: d.description_fr, description_en: d.description_en,
          category: d.category, file_url: d.file_url,
          format: d.format, size: d.size, published: d.published,
          featured: d.featured,
        });
      } else {
        await api.post("/admin/documents", {
          title_fr: d.title_fr, title_en: d.title_en,
          description_fr: d.description_fr, description_en: d.description_en,
          category: d.category, file_url: d.file_url,
          format: d.format, size: d.size, published: d.published,
          featured: d.featured,
        });
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || (isFr ? "Échec de l'enregistrement" : "Save failed"));
    }
  };

  return (
    <div className="admin-card space-y-4" data-testid="doc-editor">
      <h3 className="font-display text-xl">
        {isCreate ? (isFr ? "Nouveau document" : "New document") : (isFr ? "Modifier le document" : "Edit document")}
      </h3>

      <div>
        <label className="label-caps">{isFr ? "Fichier" : "File"}</label>
        <div className="flex items-center gap-3 mt-2">
          <input
            ref={fileInput}
            type="file"
            data-testid="doc-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="font-body text-sm"
          />
          {uploading && <span className="font-body text-xs italic">{isFr ? "Envoi…" : "Uploading…"}</span>}
        </div>
        {d.file_url && !uploading && (
          <div className="mt-2 font-body text-xs" style={{ color: "var(--muted)" }}>
            ✓ {d.file_url.split("/").pop()} · {(d.format || "").toUpperCase()} {d.size > 0 && `· ${formatSize(d.size)}`}
          </div>
        )}
        <div className="font-body text-xs mt-1" style={{ color: "var(--muted)" }}>
          {isFr ? "PDF, Word, Excel, images, audio, vidéo… (50 Mo max)" : "PDF, Word, Excel, images, audio, video… (50 MB max)"}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-caps">{isFr ? "Titre (FR) *" : "Title (FR) *"}</label>
          <input className="input-classic" data-testid="doc-title-fr"
                 value={d.title_fr} onChange={(e) => set("title_fr", e.target.value)} />
        </div>
        <div>
          <label className="label-caps">{isFr ? "Titre (EN)" : "Title (EN)"}</label>
          <input className="input-classic" data-testid="doc-title-en"
                 value={d.title_en} onChange={(e) => set("title_en", e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-caps">{isFr ? "Description (FR)" : "Description (FR)"}</label>
          <textarea rows={3} className="input-classic" data-testid="doc-desc-fr"
                    value={d.description_fr} onChange={(e) => set("description_fr", e.target.value)} />
        </div>
        <div>
          <label className="label-caps">{isFr ? "Description (EN)" : "Description (EN)"}</label>
          <textarea rows={3} className="input-classic" data-testid="doc-desc-en"
                    value={d.description_en} onChange={(e) => set("description_en", e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-caps">{isFr ? "Catégorie *" : "Category *"}</label>
          <select className="input-classic" data-testid="doc-category"
                  value={d.category} onChange={(e) => set("category", e.target.value)}>
            {categories.map((c) => (
              <option key={c} value={c}>{labels[c] || c}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-2 justify-end">
          <label className="flex items-center gap-2 font-body text-sm">
            <input type="checkbox" data-testid="doc-published"
                   checked={d.published} onChange={(e) => set("published", e.target.checked)} />
            {isFr ? "Publié (visible sur le site public)" : "Published (visible on public site)"}
          </label>
          <label className="flex items-center gap-2 font-body text-sm">
            <input type="checkbox" data-testid="doc-featured-input"
                   checked={d.featured} onChange={(e) => set("featured", e.target.checked)} />
            <span style={{ color: "var(--gold)" }}>★</span>{" "}
            {isFr ? "À la une (épingler en haut de la page)" : "Featured (pin at the top of the page)"}
          </label>
        </div>
      </div>

      {error && <div className="font-body text-sm" style={{ color: "var(--cinnabar)" }}>{error}</div>}

      <div className="flex gap-3 justify-end">
        <button className="btn btn-outline" onClick={onCancel} data-testid="doc-cancel">
          {isFr ? "Annuler" : "Cancel"}
        </button>
        <button className="btn btn-primary" onClick={save} data-testid="doc-save" disabled={uploading}>
          {isFr ? "Enregistrer" : "Save"}
        </button>
      </div>
    </div>
  );
}
